import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Upload, FileText, Sparkles, Trash2, Loader2, ChevronRight, Lock } from "lucide-react";
import ReportViewer from "./ReportViewer";

export default function MatterDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const [matter, setMatter] = useState(null);
  const [docs, setDocs] = useState([]);
  const [reports, setReports] = useState([]);
  const [usage, setUsage] = useState(null);
  const [activeTab, setActiveTab] = useState("outputs"); // facts | docs | outputs
  const [openReportId, setOpenReportId] = useState(null);
  const [question, setQuestion] = useState("");
  const [language, setLanguage] = useState(lang);
  const [includeDocs, setIncludeDocs] = useState(true);
  const [running, setRunning] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [m, d, r, u] = await Promise.all([
        api.get(`/matters/${id}`),
        api.get(`/matters/${id}/documents`),
        api.get(`/matters/${id}/reports`),
        api.get("/usage/me"),
      ]);
      setMatter(m.data); setDocs(d.data); setReports(r.data); setUsage(u.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    }
  };
  useEffect(() => { load(); }, [id]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append("file", f);
    try {
      await api.post(`/matters/${id}/documents`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("common.success"));
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.message) ? detail.message : (detail || t("common.error"));
      toast.error(msg);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const runAnalysis = async (e) => {
    e.preventDefault();
    if (question.trim().length < 5) return;
    setRunning(true);
    try {
      const res = await api.post("/analyses", {
        matter_id: id, question, include_documents: includeDocs, language,
      });
      toast.success(t("common.success"));
      setQuestion("");
      setOpenReportId(res.data.id);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "quota_exceeded") {
        toast.error(detail.message, { action: { label: lang === "el" ? "Αναβάθμιση" : "Upgrade", onClick: () => nav("/pricing") } });
      } else {
        toast.error((detail && (detail.message || detail)) || t("common.error"));
      }
    } finally {
      setRunning(false);
    }
  };

  const removeMatter = async () => {
    if (!window.confirm(lang === "el" ? "Διαγραφή υπόθεσης;" : "Delete this matter?")) return;
    await api.delete(`/matters/${id}`);
    toast.success(t("common.success"));
    nav("/matters");
  };

  if (!matter) return <DashboardLayout><div className="text-slate-500">{t("common.loading")}</div></DashboardLayout>;

  const openReport = reports.find((r) => r.id === openReportId);

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <Link to="/matters" className="text-sm text-slate-500 hover:text-slate-800">{t("dash.matters")}</Link>
          <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl text-slate-900 tracking-tight break-words">{matter.title}</h1>
          {matter.tags?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {matter.tags.map((tg) => (
                <span key={tg} className="text-[11px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{tg}</span>
              ))}
            </div>
          )}
        </div>
        <button onClick={removeMatter} className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1" data-testid="delete-matter">
          <Trash2 className="h-4 w-4" /> {t("common.delete")}
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-2 border-b border-slate-200">
        {[
          { k: "outputs", label: t("matter.tabOutputs") },
          { k: "docs",    label: t("matter.tabDocs") },
          { k: "facts",   label: t("matter.tabFacts") },
        ].map((tb) => (
          <button
            key={tb.k}
            onClick={() => setActiveTab(tb.k)}
            data-testid={`tab-${tb.k}`}
            className={`px-4 py-2.5 -mb-px text-sm font-medium border-b-2 transition ${
              activeTab === tb.k ? "border-[#0A192F] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {activeTab === "facts" && (
          <div className="card-premium p-8 max-w-3xl whitespace-pre-wrap text-slate-700 leading-relaxed" data-testid="facts-view">
            {matter.facts || <span className="text-slate-400">—</span>}
          </div>
        )}

        {activeTab === "docs" && (
          <div className="max-w-4xl">
            <div className="card-premium p-6">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-display font-bold text-lg text-slate-900">{t("matter.uploadDoc")}</div>
                  <div className="text-xs text-slate-500 mt-1">PDF / DOCX / TXT · max 10 MB</div>
                </div>
                {usage && !usage.features?.file_upload ? (
                  <Link to="/pricing" className="rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900 inline-flex items-center gap-2 hover:bg-amber-100"
                        data-testid="upload-locked">
                    <Lock className="h-3.5 w-3.5" />
                    {lang === "el" ? "Αναβάθμιση σε Professional για μεταφόρτωση" : "Upgrade to Professional to upload"}
                  </Link>
                ) : (
                  <div>
                    <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" onChange={upload} className="hidden" id="fileup" data-testid="doc-upload-input" />
                    <label htmlFor="fileup" className="btn-primary rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 cursor-pointer">
                      <Upload className="h-4 w-4" /> {t("matter.uploadDoc")}
                    </label>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-6 space-y-3">
              {docs.length === 0 ? (
                <div className="text-sm text-slate-500" data-testid="docs-empty">{t("matter.noDocs")}</div>
              ) : (
                docs.map((d) => (
                  <div key={d.id} className="card-premium p-5 flex items-center gap-4" data-testid={`doc-${d.id}`}>
                    <div className="h-10 w-10 rounded-lg bg-[#0A192F]/5 text-[#0A192F] flex items-center justify-center">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{d.filename}</div>
                      <div className="text-xs text-slate-500">
                        {(d.size_bytes / 1024).toFixed(1)} KB ·
                        <span className={`ml-1 ${d.status === "ready" ? "text-emerald-600" : d.status === "failed" ? "text-red-600" : "text-amber-600"}`}>
                          {d.status}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={async () => { await api.delete(`/documents/${d.id}`); load(); }}
                      className="text-slate-400 hover:text-red-600 transition"
                      data-testid={`doc-delete-${d.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === "outputs" && (
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <form onSubmit={runAnalysis} className="card-premium p-6 space-y-5" data-testid="run-form">
                <div>
                  <div className="kicker !text-slate-500 mb-2">{t("matter.question")}</div>
                  <textarea
                    rows={6} required minLength={5} value={question} onChange={(e) => setQuestion(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F] outline-none"
                    placeholder={lang === "el"
                      ? "π.χ. Ποια είναι τα δικαιώματα του εργαζομένου σε περίπτωση καταχρηστικής απόλυσης στην Κύπρο;"
                      : "e.g. What are the employee's rights in case of wrongful dismissal in Cyprus?"}
                    data-testid="run-question"
                  />
                </div>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <label className="text-sm text-slate-700 inline-flex items-center gap-2">
                    <input type="checkbox" checked={includeDocs} onChange={(e) => setIncludeDocs(e.target.checked)} data-testid="run-include-docs" />
                    {t("matter.includeDocs")}
                  </label>
                  <select value={language} onChange={(e) => setLanguage(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="run-language">
                    <option value="el">Ελληνικά</option>
                    <option value="en">English</option>
                  </select>
                </div>
                <button disabled={running} className="btn-primary w-full rounded-full px-6 py-3 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="run-submit">
                  {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {running ? t("matter.generating") : t("matter.generate")}
                </button>
              </form>
            </div>

            <div className="lg:col-span-7">
              {reports.length === 0 ? (
                <div className="card-premium p-10 text-center" data-testid="outputs-empty">
                  <div className="text-slate-500 text-sm">{t("matter.empty")}</div>
                </div>
              ) : (
                <div className="space-y-3">
                  {reports.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setOpenReportId(r.id)}
                      className="w-full card-premium p-5 flex items-center gap-4 text-left hover:border-[#0A192F]/40"
                      data-testid={`report-${r.id}`}
                    >
                      <div className="h-10 w-10 rounded-lg bg-[#8B1E3F]/10 text-[#8B1E3F] flex items-center justify-center">
                        <Sparkles className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 line-clamp-1">{r.question}</div>
                        <div className="mt-1 text-xs text-slate-500 flex gap-3">
                          <span className={r.status === "ready" ? "text-emerald-600" : r.status === "failed" ? "text-red-600" : "text-amber-600"}>{r.status}</span>
                          <span>{new Date(r.created_at).toLocaleString()}</span>
                          <span className="uppercase">{r.language}</span>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {openReport && (
        <ReportViewer report={openReport} onClose={() => setOpenReportId(null)} />
      )}
    </DashboardLayout>
  );
}
